import { LoginRequestStatuses } from '@shared/interfaces/ILoginReducerState';
import { ILoggedUser } from '@shared/interfaces/IUser';
import { ILocalStorageUserData } from '@shared/interfaces/IUserService';
import { Environments } from '@shared/constants/environments';
import { initPostHog } from '@shared/utils/posthog.utils';
import { IconNames } from '@shared/components/TrusstorIconShared/IconNames.enum';
import { useIconQuery } from '@shared/components/TrusstorIconShared/TrusstorIconShared';
import { assertMonitoringConfig, initMonitoringService } from '@shared/utils/monitoring.util';
import { authService, requestService, streamService, userService, versionService } from '@src/servicesInitializer';
import { customHistory } from '@shared/utils/history.utils';
import { store } from '../store/store';
import { setLoginStateData } from '../store/slices/login.slice';
import { config } from '../config/config';
import { AppRoutes } from '../constants/appRoutes.enum';
import { errorInterceptor } from './request.utils';
import {
	IAuthThunkDispatch,
	BetterAuthCompletionOutcome,
	completeBetterAuthLogin,
	logout,
	setLoggedUserThunk,
} from '../store/thunks/login.thunks';

/**
 * The store's `dispatch` as the auth thunks need it. `store` is annotated `EnhancedStore`, an
 * annotation that erases the thunk middleware from `dispatch`'s type — which is why the rest of
 * this file casts. The cutover paths below go through this view instead.
 */
const dispatchAuthThunk: IAuthThunkDispatch = store.dispatch as unknown as IAuthThunkDispatch;

export const initRequestService = (): void => {
	requestService.addResponseErrorInterceptor(errorInterceptor(config));
};

export const initVersionService = (): void => {
	const currentVersion: string | null = versionService.getVersion();
	if (!currentVersion) {
		versionService.setVersion(import.meta.env.REACT_APP_DASHBOARD_VERSION || '');
	}
};

/**
 * "This client has no usable session" — tear down what is stored HERE and land on a login screen.
 *
 * Used by the 401 interceptor (a refresh that cannot be refreshed, a request that exhausted its
 * retries) and by a protected route rendered without an access token. Deliberately does NOT revoke
 * the engine session: that cookie is host-only on the gateway origin, so it is ONE session shared
 * with the desktop app — the very premise the silent bootstrap below is built on — and revoking it
 * over a local, transient token problem in this tab would sign the user out of a desktop tab that
 * was working fine. Revocation belongs to the deliberate logout only.
 */
export const handleSessionExpiredAndRedirect = (): void => {
	dispatchAuthThunk(logout());
	customHistory.replace(AppRoutes.login);
};

/**
 * The user asked to be signed out.
 *
 * The engine session is revoked FIRST: since WP3 it IS the refresh credential, so clearing only
 * local state leaves the httpOnly cookie and its Redis+DB session alive — a shared phone keeps a
 * live session after "log out", and the silent bootstrap below would log the next person straight
 * back in as the previous one. Best-effort by construction: a dead session or a network blip must
 * never trap someone in a logged-in UI, so the local teardown runs in `finally` regardless. It goes
 * through better-auth's own client, not `requestService`, so it does not re-enter the 401
 * interceptor.
 */
const revokeEngineSessionAndTearDown = async (): Promise<void> => {
	try {
		const { authClient } = await import('@src/services/betterAuthClient');
		await authClient.signOut();
	} catch {
		// Swallowed — see above.
	} finally {
		handleSessionExpiredAndRedirect();
	}
};

export const handleLogoutAndRedirect = (): void => {
	void revokeEngineSessionAndTearDown();
};

/**
 * Boot with no stored tokens: try the bridge before showing a login screen.
 *
 * The engine session cookie is shared with the desktop app (both apps call the same gateway
 * origin, so it is one host-only cookie serving both), which means a user who signed in on
 * desktop and followed a link to mobile already has everything needed for tokens. This is what
 * replaced the `?token=` handoff: cross-app continuity without a credential in a URL.
 *
 * A rejected bridge call is the ordinary "nobody is signed in" case, not an error — it must land
 * on the login screen, so the request status goes back to INITIAL. Leaving it ERROR would render
 * `App`'s blank `<div/>` instead of the login page.
 */
const bootstrapFromEngineSession = async (): Promise<void> => {
	store.dispatch(
		setLoginStateData({
			loginRequestStatus: {
				state: LoginRequestStatuses.LOADING,
			},
		})
	);

	const outcome: BetterAuthCompletionOutcome = await dispatchAuthThunk(
		completeBetterAuthLogin({ history: customHistory })
	);

	if (outcome === 'error') {
		store.dispatch(
			setLoginStateData({
				loginRequestStatus: {
					state: LoginRequestStatuses.INITIAL,
				},
			})
		);
		customHistory.replace(AppRoutes.login);
	}
};

export const handleUserLoggedOut = (): void => {
	const lsUserData: ILocalStorageUserData | null = userService.getLSUserData();

	if (!lsUserData?.accessToken || !lsUserData?.refreshToken) {
		// The URL is never read for credentials — the `?token=` handoff is exactly what this
		// app's own login replaces.
		void bootstrapFromEngineSession();
	}
};

const initUserData = async (): Promise<void> => {
	try {
		await authService.refreshUserToken();
	} catch (e) {
		store.dispatch(logout() as any);
		window.location.reload();
	}
};

const initStreamService = (): void => {
	streamService.setOnOpenStreamInterceptor(async () => {
		const isTokenExpired: boolean = await authService.getIsTokenExpired();
		if (isTokenExpired) {
			await authService.refreshUserToken();
		}
	});
	// Reconnect path: EventSource cannot rotate its query-string token, so on error the
	// stream is re-opened — after making sure the token it will carry is fresh.
	streamService.setOnErrorStreamInterceptor(async () => {
		const isTokenExpired: boolean = await authService.getIsTokenExpired();
		if (isTokenExpired) {
			await authService.refreshUserToken();
		}
	});
};

export const initImportantIcons = (): void => {
	const iconsNames: IconNames[] = [
		IconNames.wifiNoConnection,
		IconNames.refresh,
		IconNames.checkCircle,
		IconNames.warningTriangle,
		IconNames.star,
		IconNames.camera,
		IconNames.pin,
		IconNames.manager,
		IconNames.helmet,
		IconNames.plus,
	];

	iconsNames.forEach((iconName: IconNames) => {
		useIconQuery(requestService, iconName);
	});
};

export const initializeBootstrapUtilities = (): void => {
	initRequestService();
	handleUserLoggedOut();
	initUserData(); // important to be after initRequestService and handleUserLoggedOut
	initMonitoringService(
		config.sentryDsn,
		import.meta.env.REACT_APP_STAGE as string,
		import.meta.env.REACT_APP_DASHBOARD_VERSION as string,
		config.sentryOrganization,
		config.sentryProjectId
	);
	initStreamService();
	initPostHog(config.posthogAPIKey, import.meta.env.REACT_APP_STAGE as Environments);
	// After both SDKs are up, so a present Sentry DSN can carry the report about a missing
	// PostHog key.
	assertMonitoringConfig(
		{ sentryDsn: config.sentryDsn, posthogAPIKey: config.posthogAPIKey },
		import.meta.env.REACT_APP_STAGE as string
	);
	initVersionService();
};
